import * as ROUTES from "shared/utils/routes";
import { DashboardRedirect, getDashboardStartPath } from "shared/utils/routes";

import "app/app.css";
import { AppProviders } from "app/AppProviders";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { PuzzelCallback } from "app/PuzzelAuth/PuzzelCallback";
import { DashboardLayout } from "features/CenterContent/DashboardLayout";
import DecisionSupport from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupport";
import { RoleContent } from "features/CenterContent/RoleContent/RoleContent";
import AlternativeTransportDetails from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/AlternativeTransportDetails";
import BackupAlternativeTransportDetails from "features/CenterContent/VehicleDetails/BackupAlternativeTransportDetails/BackupAlternativeTransportDetails";
import ReserveAlternativeTransportDetails from "features/CenterContent/VehicleDetails/ReserveAlternativeTransportDetails/ReserveAlternativeTransportDetails";
import TrainDetails from "features/CenterContent/VehicleDetails/TrainDetails/TrainDetails";
import Header from "features/Header/Header";
import { HistoryDetailRoute } from "features/History/HistoryDetail/HistoryDetailRoute";
import { HistoryOverview } from "features/History/HistoryOverview";
import { OploggPage } from "features/OploggPage/OploggPage";
import { VersionPage } from "features/VersionPage/VersionPage";
import { FC } from "react";
import {
  generatePath,
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useParams,
} from "react-router-dom";
import { UserRole } from "shared/types/roles";
import { useDropsRole } from "stores/useDropsRole";
import styled from "styled-components";

const AppGrid = styled.div`
  display: grid;
  grid-template-areas:
    "header header header"
    "vehicleAffected main todoOplogg";
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto 1fr;
  gap: 12px;
  padding-bottom: 12px;
  padding-top: 12px;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colorBackgroundBase};
  overflow-x: auto;
`;

// Used to redirect open VBS tabs from the old url to the new. Only relevant for a shift or two after merge.
const TemporaryRedirect: FC = () => {
  const { infrastructureEventId } = useParams();

  return (
    <Navigate
      to={
        infrastructureEventId
          ? generatePath(ROUTES.ALTERNATIVE_TRANSPORT_DECISION_SUPPORT, {
              uuid: infrastructureEventId,
            })
          : ROUTES.DASHBOARD
      }
      replace
    />
  );
};

const DashboardShell: FC<{ role: UserRole }> = ({ role }) => (
  <AppGrid>
    <Header />
    <Routes>
      <Route path={ROUTES.DASHBOARD} element={<DashboardLayout />}>
        <Route path={ROUTES.LANDING} element={<RoleContent />} />
        <Route path={ROUTES.TRAIN} element={<TrainDetails />} />
        <Route
          path={ROUTES.ALTERNATIVE_TRANSPORT_DECISION_SUPPORT}
          element={<DecisionSupport />}
        />
        <Route path={ROUTES.BUS} element={<AlternativeTransportDetails />} />
        <Route
          path={ROUTES.RESERVEBUS}
          element={<ReserveAlternativeTransportDetails />}
        />

        <Route
          path={ROUTES.BACKUPBUS}
          element={<BackupAlternativeTransportDetails />}
        />

        <Route
          // TODO: Remove this in a few days, this redirect is only relevant
          // for tabs opened before deploy within a single shift
          path={ROUTES.ALTERNATIVE_TRANSPORT_INFRASTRUCTURE_EVENT_DEPRECATED}
          element={<TemporaryRedirect />}
        />

        <Route
          // Match redirect from root with no path (i.e. to /dashboard/)
          index
          element={<Navigate to={getDashboardStartPath(role)} replace />}
        />
        <Route
          // Match redirect with path (e.g. /dashboard/whatever)
          // This is necessary in addition to the index route above
          // in order for old links to keep working.
          path="*"
          element={<Navigate to={getDashboardStartPath(role)} replace />}
        />
      </Route>

      <Route path={ROUTES.HISTORY} element={<Outlet />}>
        <Route index element={<HistoryOverview />} />
        <Route
          path="detail/:eventTypeParam/:eventUuidParam"
          element={<HistoryDetailRoute />}
        />
      </Route>

      <Route path={ROUTES.OPLOGG} element={<Outlet />}>
        <Route index element={<OploggPage />} />
      </Route>

      <Route path={ROUTES.VERSION} element={<Outlet />}>
        <Route index element={<VersionPage />} />
      </Route>

      <Route
        path="*"
        element={
          // Attempt to forward routes into /dashboard/ to be backwards compatible
          // (before history was added, dashboard lived at the root route)
          <DashboardRedirect />
        }
      />
    </Routes>
  </AppGrid>
);

const AuthorizedApp = () => {
  const { role } = useDropsRole();
  return (
    <AppProviders>
      <Router>
        <ErrorBoundary>
          <Routes>
            <Route path={ROUTES.PUZZEL_CALLBACK} element={<PuzzelCallback />} />
            <Route path="/*" element={<DashboardShell role={role} />} />
          </Routes>
        </ErrorBoundary>
      </Router>
    </AppProviders>
  );
};

export default AuthorizedApp;
